import { ref } from "vue";
import { GQL_URL } from "../utils/APIs";

// Cached user email for legacy references; logUserAction will re-read localStorage dynamically
export const userEmail = localStorage.getItem("email");

// Global reactive input fields preserved for backwards compatibility with existing components
export let userLogInputFields = ref({
  userEmail: null,
  description: null,
  action: null,
  method: null,
  statusCode: 0,
  environment: null,
  endpoint: null,
  path: null,
});

/**
 * Standard Action Name Constants
 * Using standard constants prevents typos and ensures consistent log aggregation.
 */
export const USER_ACTIONS = {
  // Data Dashboards
  VIEW_DASHBOARD: "ViewDataDashboard",
  SEARCH_DASHBOARD: "SearchDataDashboard",
  FILTER_DASHBOARD: "FilterDataDashboard",
  DOWNLOAD_DASHBOARD_DATA: "DownloadDashboardData",
  DOWNLOAD_DASHBOARD_GRAPH: "DownloadDashboardGraph",

  // Asset Profiles
  VIEW_PROFILES: "ViewAssetProfiles",
  VIEW_ASSET_PROFILE: "ViewAssetProfile",
  VIEW_COMPANY_PROFILE: "ViewCompanyProfile",
  VIEW_COUNTRY_PROFILE: "ViewCountryProfile",
  FILTER_PROFILES: "FilterAssetProfiles",
  DOWNLOAD_PROFILES_DATA: "DownloadAssetProfilesData",
  DOWNLOAD_PROFILES_GRAPH: "DownloadAssetProfilesGraph",

  // Pricing Services
  VIEW_PRICING: "ViewPricingServices",
  FILTER_PRICING: "FilterPricingServices",
  DOWNLOAD_PRICING_REPORT: "DownloadPricingReport",

  // Trade Flow
  VIEW_TRADE_FLOW: "ViewTradeFlow",
  SEARCH_TRADE_FLOW: "SearchTradeFlow",
  FILTER_TRADE_FLOW: "FilterTradeFlow",
  DOWNLOAD_TRADE_FLOW_GRAPH: "DownloadTradeFlowGraph",
};

export const query = `mutation ($userActionLog: UserActionLogInput!) {
    upsertUserActionLog(input: { userActionLog: $userActionLog }) {
      userActionLog {
        id
      }
    }
  }`;

/**
 * Core logging utility to record user activities to GraphQL user_action_logs.
 * Automatically resolves current user email, environment, and path.
 * Truncates descriptions to <= 255 characters to respect the database schema.
 * All calls are wrapped in try/catch to ensure logging never interrupts user workflows.
 *
 * @param {string} action - Action identifier (e.g. USER_ACTIONS.VIEW_DASHBOARD)
 * @param {string} description - Human-readable summary of the action
 * @param {object} [meta={}] - Optional metadata overrides (endpoint, path, method, statusCode)
 */
export const logUserAction = async (action, description, meta = {}) => {
  try {
    const email = localStorage.getItem("email") || userEmail || "anonymous";

    // Ensure description conforms to the database column limit (VARCHAR 255)
    const safeDescription = description && description.length > 255
      ? description.substring(0, 252) + "..."
      : description;

    const payload = {
      userEmail: email,
      action: action,
      description: safeDescription,
      environment: window.location.hostname,
      path: meta.path || window.location.pathname || null,
      endpoint: meta.endpoint || null,
      method: meta.method || null,
      statusCode: meta.statusCode || 200,
    };

    // Keep reactive ref in sync for any components observing userLogInputFields
    userLogInputFields.value = payload;

    const response = await fetch(GQL_URL.BASE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer TOKEN`,
        "X-Api-Key": GQL_URL.API_KEY,
      },
      body: JSON.stringify({
        query,
        variables: {
          userActionLog: payload,
        },
      }),
    });

    const data = await response.json();
    if (data?.errors) {
      console.warn("[UserLog] GraphQL returned errors:", data.errors);
    }
    return data;
  } catch (err) {
    // Non-blocking catch to ensure logging never breaks UI interactions
    console.warn("[UserLog] Logging failed:", action, err);
  }
};

/**
 * Debounced logging utility for high-frequency user actions like search inputs and filter changes.
 * Ensures only the latest action is logged after user interaction pauses.
 *
 * @param {string} action - Action identifier
 * @param {string|Function} descriptionOrGetter - Description string or getter function
 * @param {number} [waitMs=500] - Debounce delay in milliseconds
 */
const debounceTimers = {};
export const logUserActionDebounced = (action, descriptionOrGetter, waitMs = 500) => {
  if (debounceTimers[action]) {
    clearTimeout(debounceTimers[action]);
  }

  debounceTimers[action] = setTimeout(() => {
    const description = typeof descriptionOrGetter === "function"
      ? descriptionOrGetter()
      : descriptionOrGetter;
    logUserAction(action, description);
    delete debounceTimers[action];
  }, waitMs);
};

/**
 * Backwards-compatible saveToUserLogs function for legacy callers.
 * Accepts an optional payload, falling back to userLogInputFields.value.
 */
export const saveToUserLogs = async (customPayload = null) => {
  const payload = customPayload || userLogInputFields.value;
  return logUserAction(payload.action, payload.description, payload);
};